import { useState, type FormEvent } from 'react';
import { ArrowLeft, RefreshCw, Settings, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MIN_PASSWORD_LENGTH,
  type AgentAdmin,
  type CreateAgentInput,
} from '@/features/agents/agents.api';
import {
  useAgentLoginEvents,
  useAgentsAdmin,
  useCreateAgent,
  useDeleteAgent,
  useResetAgentPassword,
  useSetAgentDisabled,
} from '@/features/agents/useAgentsAdmin';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';

interface ManageAgentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The dialog body's current view. `roster` is the agent list; `add` is the
 *  create-agent form; `log` is one agent's login history. */
type View = 'roster' | 'add' | 'log';

/**
 * Admin-only agent-management dialog (opened from the sidebar header's
 * admin-gated icon button; UI copy is Chinese — title 管理坐席).
 *
 * Roster view — one row per console agent (online light, name, email, role,
 * active/disabled pill) plus its actions:
 *
 *   - 禁用/恢复 — disabling is destructive to a colleague's session (immediate
 *     force logout + login barred), so it takes an explicit inline two-step
 *     confirm (no timers — the confirm state only changes on a click or on
 *     dialog close). Enabling is one click. The signed-in admin's own row and
 *     any admin-role row are protected (受保护) — the Hub forbids disabling both.
 *   - 修改 — inline reset-password form (new + confirm, min 8, mismatch blocks).
 *   - Delete mode (gear → 删除坐席) reveals a 删除 affordance per row with the
 *     same two-step confirm; a 409-has-history steers the admin to disable.
 *   - Clicking a row body opens that agent's login log (time / IP / device).
 *     Inner action buttons stopPropagation so they never open the log.
 *
 * The gear menu (top-right) hosts 新增坐席 / 删除坐席 / 坐席登录log. The roster
 * query is gated on `open` (never fires closed, never for non-admins who can't
 * mount this dialog) and polls modestly while open so the online lights stay
 * live — a poll, not a WS subscription, to avoid PR #20's render-loop class of
 * bug. Mutation failures surface the backend `detail` via toast.
 */
export function ManageAgentsDialog({
  open,
  onOpenChange,
}: ManageAgentsDialogProps): React.JSX.Element {
  const selfId = useAuthStore((s) => s.agent?.id);
  const query = useAgentsAdmin({ enabled: open });
  const disableMutation = useSetAgentDisabled();
  const deleteMutation = useDeleteAgent();

  const [view, setView] = useState<View>('roster');
  const [logAgent, setLogAgent] = useState<AgentAdmin | null>(null);
  const [deleteMode, setDeleteMode] = useState(false);

  // Single-active-row states (only one row can be mid-interaction at a time).
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deleteConfirmingId, setDeleteConfirmingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  function resetTransientState(): void {
    setConfirmingId(null);
    setDeleteConfirmingId(null);
    setEditingId(null);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      // Return to a clean slate so the next open starts on the roster.
      setView('roster');
      setLogAgent(null);
      setDeleteMode(false);
      resetTransientState();
    }
    onOpenChange(nextOpen);
  }

  function openLog(agent: AgentAdmin): void {
    resetTransientState();
    setLogAgent(agent);
    setView('log');
  }

  function backToRoster(): void {
    setLogAgent(null);
    setView('roster');
  }

  function setDisabled(agentId: string, disabled: boolean): void {
    disableMutation.mutate(
      { agentId, disabled },
      { onSettled: () => setConfirmingId(null) },
    );
  }

  function deleteAgentRow(agent: AgentAdmin): void {
    deleteMutation.mutate(
      { agentId: agent.id, display_name: agent.display_name },
      { onSettled: () => setDeleteConfirmingId(null) },
    );
  }

  const disablePendingId = disableMutation.isPending
    ? (disableMutation.variables?.agentId ?? null)
    : null;
  const deletePendingId = deleteMutation.isPending
    ? (deleteMutation.variables?.agentId ?? null)
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle>
                {view === 'add' && '新增坐席'}
                {view === 'log' && `登录记录 · ${logAgent?.display_name ?? ''}`}
                {view === 'roster' && '管理坐席'}
              </DialogTitle>
              <DialogDescription>
                {view === 'add' && '创建一个新的控制台坐席账号。'}
                {view === 'log' && '该坐席的成功登录记录(最新在前):时间 · IP · 设备。'}
                {view === 'roster' &&
                  (deleteMode
                    ? '删除模式:仅无历史记录的坐席可删除,有记录的请改用禁用。'
                    : '禁用后该坐席立即被登出且无法再登录;可随时恢复。点击行查看登录记录。')}
              </DialogDescription>
            </div>

            {view === 'roster' && (
              <RosterGearMenu
                deleteMode={deleteMode}
                onAdd={() => {
                  resetTransientState();
                  setView('add');
                }}
                onToggleDeleteMode={() => {
                  resetTransientState();
                  setDeleteMode((v) => !v);
                }}
                onLoginLogHint={() =>
                  toast('点击任意坐席行即可查看其登录记录', { id: 'agent-login-log-hint' })
                }
              />
            )}
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {view === 'add' && (
            <AddAgentForm onDone={() => setView('roster')} onCancel={() => setView('roster')} />
          )}

          {view === 'log' && logAgent && (
            <LoginLogView agent={logAgent} open={open && view === 'log'} onBack={backToRoster} />
          )}

          {view === 'roster' && (
            <AgentListBody
              query={query}
              selfId={selfId}
              deleteMode={deleteMode}
              confirmingId={confirmingId}
              deleteConfirmingId={deleteConfirmingId}
              editingId={editingId}
              disablePendingId={disablePendingId}
              deletePendingId={deletePendingId}
              onOpenLog={openLog}
              onRequestDisable={(id) => {
                setDeleteConfirmingId(null);
                setEditingId(null);
                setConfirmingId(id);
              }}
              onCancelDisable={() => setConfirmingId(null)}
              onSetDisabled={setDisabled}
              onRequestDelete={(id) => {
                setConfirmingId(null);
                setEditingId(null);
                setDeleteConfirmingId(id);
              }}
              onCancelDelete={() => setDeleteConfirmingId(null)}
              onConfirmDelete={deleteAgentRow}
              onRequestEdit={(id) => {
                setConfirmingId(null);
                setDeleteConfirmingId(null);
                setEditingId(id);
              }}
              onCancelEdit={() => setEditingId(null)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// RosterGearMenu -------------------------------------------------------------

interface RosterGearMenuProps {
  deleteMode: boolean;
  onAdd: () => void;
  onToggleDeleteMode: () => void;
  onLoginLogHint: () => void;
}

function RosterGearMenu({
  deleteMode,
  onAdd,
  onToggleDeleteMode,
  onLoginLogHint,
}: RosterGearMenuProps): React.JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="坐席管理菜单"
          className="size-8 shrink-0"
        >
          <Settings className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onAdd}>
          <UserPlus className="mr-2 size-4" />
          新增坐席
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onToggleDeleteMode}>
          <Trash2 className="mr-2 size-4" />
          {deleteMode ? '退出删除模式' : '删除坐席'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLoginLogHint}>坐席登录log</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// AddAgentForm ---------------------------------------------------------------

function AddAgentForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const mutation = useCreateAgent();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'agent' | 'admin'>('agent');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`密码至少 ${MIN_PASSWORD_LENGTH} 位`);
      return;
    }
    if (password !== confirm) {
      setError('两次输入的密码不一致');
      return;
    }
    setError(null);

    const input: CreateAgentInput = {
      email: email.trim(),
      display_name: displayName.trim(),
      role,
      password,
    };
    // On success return to the roster; on failure the hook's toast explains and
    // the form stays put (with its values) for a deliberate retry.
    mutation.mutate(input, { onSuccess: () => onDone() });
  }

  return (
    <form className="flex flex-col gap-3 py-1" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-agent-email">邮箱</Label>
        <Input
          id="new-agent-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-agent-name">显示名</Label>
        <Input
          id="new-agent-name"
          type="text"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-agent-role">角色</Label>
        <select
          id="new-agent-role"
          value={role}
          onChange={(e) => setRole(e.target.value as 'agent' | 'admin')}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="agent">坐席 (agent)</option>
          <option value="admin">管理员 (admin)</option>
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-agent-password">密码</Label>
        <Input
          id="new-agent-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-agent-confirm">确认密码</Label>
        <Input
          id="new-agent-confirm"
          type="password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      {error && <p className="text-xs font-semibold text-destructive">{error}</p>}

      <div className="mt-1 flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          创建
        </Button>
      </div>
    </form>
  );
}

// LoginLogView ---------------------------------------------------------------

function LoginLogView({
  agent,
  open,
  onBack,
}: {
  agent: AgentAdmin;
  open: boolean;
  onBack: () => void;
}): React.JSX.Element {
  const query = useAgentLoginEvents(agent.id, { enabled: open });

  return (
    <div className="flex flex-col gap-3 py-1">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="self-start"
        onClick={onBack}
      >
        <ArrowLeft className="mr-2 size-3.5" />
        返回坐席列表
      </Button>

      {query.isLoading && (
        <ul className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <li key={i} className="rounded-[12px] p-3 shadow-neu-raised">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="mt-2 h-3 w-56" />
            </li>
          ))}
        </ul>
      )}

      {query.isError && (
        <div className="flex flex-col items-start gap-2 px-1 py-4 text-sm text-muted-foreground">
          <p>登录记录加载失败</p>
          <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>
            <RefreshCw className="mr-2 size-3.5" />
            重试
          </Button>
        </div>
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <p className="px-1 py-6 text-sm text-muted-foreground">暂无登录记录</p>
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {query.data.items.map((event) => (
            <li key={event.id} className="rounded-[12px] p-3 text-sm shadow-neu-raised">
              <p className="font-semibold">{formatLoginTime(event.created_at)}</p>
              <p className="text-xs text-muted-foreground">IP:{event.ip}</p>
              <p className="truncate text-xs text-muted-foreground" title={event.user_agent ?? ''}>
                设备:{event.user_agent ?? '未知'}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Format an ISO timestamp for the login log; falls back to the raw string if
 *  the environment can't parse/localize it (keeps the row informative). */
function formatLoginTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
}

// AgentListBody --------------------------------------------------------------

interface AgentListBodyProps {
  query: ReturnType<typeof useAgentsAdmin>;
  selfId: string | undefined;
  deleteMode: boolean;
  confirmingId: string | null;
  deleteConfirmingId: string | null;
  editingId: string | null;
  disablePendingId: string | null;
  deletePendingId: string | null;
  onOpenLog: (agent: AgentAdmin) => void;
  onRequestDisable: (agentId: string) => void;
  onCancelDisable: () => void;
  onSetDisabled: (agentId: string, disabled: boolean) => void;
  onRequestDelete: (agentId: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (agent: AgentAdmin) => void;
  onRequestEdit: (agentId: string) => void;
  onCancelEdit: () => void;
}

function AgentListBody(props: AgentListBodyProps): React.JSX.Element {
  const { query } = props;

  if (query.isLoading) {
    return (
      <ul className="flex flex-col gap-2.5 py-1">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-center gap-3 rounded-[14px] p-3 shadow-neu-raised">
            <Skeleton className="size-2.5 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-8 w-16 rounded-md" />
          </li>
        ))}
      </ul>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-2 px-1 py-4 text-sm text-muted-foreground">
        <p>坐席列表加载失败</p>
        <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>
          <RefreshCw className="mr-2 size-3.5" />
          重试
        </Button>
      </div>
    );
  }

  const agents = query.data ?? [];

  if (agents.length === 0) {
    return <p className="px-1 py-6 text-sm text-muted-foreground">暂无坐席</p>;
  }

  return (
    <ul className="flex flex-col gap-2.5 py-1">
      {agents.map((agent) => (
        <AgentRow
          key={agent.id}
          agent={agent}
          isSelf={agent.id === props.selfId}
          deleteMode={props.deleteMode}
          isConfirming={props.confirmingId === agent.id}
          isDeleteConfirming={props.deleteConfirmingId === agent.id}
          isEditing={props.editingId === agent.id}
          isDisablePending={props.disablePendingId === agent.id}
          isDeletePending={props.deletePendingId === agent.id}
          onOpenLog={() => props.onOpenLog(agent)}
          onRequestDisable={() => props.onRequestDisable(agent.id)}
          onCancelDisable={props.onCancelDisable}
          onSetDisabled={(disabled) => props.onSetDisabled(agent.id, disabled)}
          onRequestDelete={() => props.onRequestDelete(agent.id)}
          onCancelDelete={props.onCancelDelete}
          onConfirmDelete={() => props.onConfirmDelete(agent)}
          onRequestEdit={() => props.onRequestEdit(agent.id)}
          onCancelEdit={props.onCancelEdit}
        />
      ))}
    </ul>
  );
}

// AgentRow -------------------------------------------------------------------

interface AgentRowProps {
  agent: AgentAdmin;
  isSelf: boolean;
  deleteMode: boolean;
  isConfirming: boolean;
  isDeleteConfirming: boolean;
  isEditing: boolean;
  isDisablePending: boolean;
  isDeletePending: boolean;
  onOpenLog: () => void;
  onRequestDisable: () => void;
  onCancelDisable: () => void;
  onSetDisabled: (disabled: boolean) => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onRequestEdit: () => void;
  onCancelEdit: () => void;
}

function AgentRow(props: AgentRowProps): React.JSX.Element {
  const { agent } = props;
  const isProtected = agent.role === 'admin' || props.isSelf;

  return (
    <li
      // Row body opens the agent's login log; inner buttons stopPropagation so
      // they never trigger this. Kept as an <li> (not role="button") so the
      // protected-rows-expose-no-button contract holds, but made keyboard- and
      // screen-reader-reachable via a link role + Enter/Space so the log isn't
      // mouse-only.
      role="link"
      tabIndex={0}
      aria-label={`查看 ${agent.display_name} 的登录记录`}
      onClick={props.onOpenLog}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onOpenLog();
        }
      }}
      className="flex cursor-pointer items-center gap-3 rounded-[14px] p-3 shadow-neu-raised hover:shadow-neu-raised-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <OnlineDot online={agent.online} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-extrabold">{agent.display_name}</p>
          <StatusPill disabled={agent.disabled} />
        </div>
        <p className="truncate text-xs text-muted-foreground">{agent.email}</p>
        <p className="text-xs capitalize text-muted-foreground">{agent.role}</p>
      </div>

      {props.isEditing ? (
        <ResetPasswordForm
          agent={agent}
          onCancel={props.onCancelEdit}
          onDone={props.onCancelEdit}
        />
      ) : (
        <div className="flex shrink-0 flex-col items-end gap-1.5" onClick={stop}>
          <AgentRowAction {...props} isProtected={isProtected} />
          {props.deleteMode && !isProtected && (
            <DeleteAction {...props} />
          )}
        </div>
      )}
    </li>
  );
}

/** Stop a click from bubbling to the row (which would open the login log). */
function stop(e: React.MouseEvent): void {
  e.stopPropagation();
}

function OnlineDot({ online }: { online: boolean }): React.JSX.Element {
  return (
    <span
      data-online={online}
      // role="img" (not "status"): a plain labelled graphic, NOT an ARIA live
      // region — otherwise the 15s roster poll would make screen readers
      // announce every dot's state on each refetch.
      role="img"
      aria-label={online ? '在线' : '离线'}
      title={online ? '在线' : '离线'}
      className={cn(
        'inline-block size-2.5 shrink-0 rounded-full',
        // Literal arbitrary classes so the JIT scanner keeps the glow.
        online ? 'bg-cyan-400 shadow-[0_0_6px_#22d3ee]' : 'bg-neutral-800',
      )}
    />
  );
}

function StatusPill({ disabled }: { disabled: boolean }): React.JSX.Element {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold',
        disabled ? 'bg-destructive/10 text-destructive' : 'bg-emerald-500/10 text-emerald-600',
      )}
    >
      {disabled ? '已禁用' : '正常'}
    </span>
  );
}

function AgentRowAction(
  props: AgentRowProps & { isProtected: boolean },
): React.JSX.Element {
  const { agent } = props;

  // Admin-role rows and the signed-in admin's own row expose no toggle — the
  // Hub forbids disabling both (403). Show the hint (and, in delete mode, the
  // reason they also can't be deleted).
  if (props.isProtected) {
    return (
      <span
        className="shrink-0 text-xs text-muted-foreground"
        title={props.isSelf ? '不能禁用自己的账号' : '不能禁用管理员账号'}
      >
        受保护
      </span>
    );
  }

  // Enabling only restores access — one click, no confirm step. (Still offer
  // 修改 so a password can be reset regardless of disabled state.)
  if (agent.disabled) {
    return (
      <div className="flex items-center gap-2">
        <EditButton onRequestEdit={props.onRequestEdit} displayName={agent.display_name} />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={props.isDisablePending}
          aria-label={`恢复 ${agent.display_name}`}
          onClick={() => props.onSetDisabled(false)}
        >
          恢复
        </Button>
      </div>
    );
  }

  // Two-step disable confirm (no timers).
  if (props.isConfirming) {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <p className="text-xs text-muted-foreground">确认后立即登出。</p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={props.isDisablePending}
            aria-label={`取消禁用 ${agent.display_name}`}
            onClick={props.onCancelDisable}
          >
            取消
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={props.isDisablePending}
            aria-label={`确认禁用 ${agent.display_name}`}
            onClick={() => props.onSetDisabled(true)}
          >
            确认禁用
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <EditButton onRequestEdit={props.onRequestEdit} displayName={agent.display_name} />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={props.isDisablePending}
        aria-label={`禁用 ${agent.display_name}`}
        onClick={props.onRequestDisable}
      >
        禁用
      </Button>
    </div>
  );
}

function EditButton({
  onRequestEdit,
  displayName,
}: {
  onRequestEdit: () => void;
  displayName: string;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      aria-label={`修改 ${displayName} 的密码`}
      onClick={onRequestEdit}
    >
      修改
    </Button>
  );
}

function DeleteAction(props: AgentRowProps): React.JSX.Element {
  const { agent } = props;

  if (props.isDeleteConfirming) {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <p className="text-xs text-muted-foreground">确认删除该坐席?</p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={props.isDeletePending}
            aria-label={`取消删除 ${agent.display_name}`}
            onClick={props.onCancelDelete}
          >
            取消
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={props.isDeletePending}
            aria-label={`确认删除 ${agent.display_name}`}
            onClick={props.onConfirmDelete}
          >
            确认删除
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="text-destructive"
      disabled={props.isDeletePending}
      aria-label={`删除 ${agent.display_name}`}
      onClick={props.onRequestDelete}
    >
      <Trash2 className="mr-1 size-3.5" />
      删除
    </Button>
  );
}

// ResetPasswordForm ----------------------------------------------------------

function ResetPasswordForm({
  agent,
  onCancel,
  onDone,
}: {
  agent: AgentAdmin;
  onCancel: () => void;
  onDone: () => void;
}): React.JSX.Element {
  const mutation = useResetAgentPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`密码至少 ${MIN_PASSWORD_LENGTH} 位`);
      return;
    }
    if (password !== confirm) {
      setError('两次输入的密码不一致');
      return;
    }
    setError(null);
    mutation.mutate(
      { agentId: agent.id, display_name: agent.display_name, password },
      { onSuccess: () => onDone() },
    );
  }

  return (
    <form className="flex w-56 shrink-0 flex-col gap-2" onClick={stop} onSubmit={handleSubmit}>
      <Input
        type="password"
        required
        placeholder="新密码"
        aria-label={`${agent.display_name} 新密码`}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />
      <Input
        type="password"
        required
        placeholder="确认新密码"
        aria-label={`${agent.display_name} 确认新密码`}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoComplete="new-password"
      />
      {error && <p className="text-xs font-semibold text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          确认修改
        </Button>
      </div>
    </form>
  );
}
